import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingBagIcon, 
  TruckIcon, 
  ShieldCheckIcon, 
  PhoneIcon,
  StarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { featuredProducts as importedFeaturedProducts } from '../resources/products/dentalProducts';
import c1 from '../client_photos/c1.jpeg';
import c2 from '../client_photos/c2.jpeg';
import c3 from '../client_photos/c3.jpeg';
import c4 from '../client_photos/c4.jpeg';
import c5 from '../client_photos/c5.jpeg';
import c6 from '../client_photos/c6.jpeg';
import c7 from '../client_photos/c7.jpeg';
import c8 from '../client_photos/c8.jpeg';
import c9 from '../client_photos/c9.jpeg';
import c10 from '../client_photos/c10.jpeg';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  
  // Client photos array
  const clientPhotos = [ c1, c2, c3, c4, c5,c6, c7, c8, c9, c10];

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      // Use featured products from JSON data
      setFeaturedProducts(importedFeaturedProducts.slice(0, 8));
    } catch (error) {
      console.error('Error loading featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <ShoppingBagIcon className="h-8 w-8" />,
      title: "2,000+ Products",
      description: "Largest collection of dental products from trusted brands",
      color: "bg-blue-500"
    },
    {
      icon: <TruckIcon className="h-8 w-8" />,
      title: "Free Shipping",
      description: "Free delivery on orders above ₹5,000 across India",
      color: "bg-green-500"
    },
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: "100% Genuine",
      description: "All products are authentic with quality guarantee",
      color: "bg-purple-500"
    },
    {
      icon: <PhoneIcon className="h-8 w-8" />,
      title: "24/7 Support",
      description: "Expert support for all your dental product needs",
      color: "bg-orange-500"
    }
  ];

  // Google Reviews - Replace with your actual Google Business reviews
  // To get your reviews: Visit your Google Business profile and copy recent reviews
  const testimonials = [
    {
      name: "Dr. Manisha Waghmare",
      role: "Dental Surgeon",
      content: "We get all dental materials ,instrument and recent tech related to dentistry.They provide fast service.trustworthy shoping experience.Thank you so much.Excellent service and genuine products. Very professional dealing. Highly recommend for all dental supplies!",
      rating: 5,
      location: "Mumbai",
      date: "3 weeks ago",
      source: "Google Review"
    },
    {
      name: "Dr. Anil r jha",
      role: "Orthodontist",
      content: "Best dental shop, All dental materials will be available and best price.",
      rating: 5,
      location: "Mumbai",
      date: "8 month ago",
      source: "Google Review"
    },
    {
      name: "Dr. Deenanath Yadav",
      role: "Clinic Owner",
      content: "excellent , prompt and committed services.",
      rating: 5,
      location: "Mumbai",
      date: "3 weeks ago",
      source: "Google Review"
    }
  ];

  // Slider state for testimonials
  const [currentReview, setCurrentReview] = useState(0);
  
  // Auto-advance slider every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextReview = () => {
    setCurrentReview((prev) => (prev + 1) % testimonials.length);
  };

  const prevReview = () => {
    setCurrentReview((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToReview = (index) => {
    setCurrentReview(index);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-20">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                India's Leading
                <span className="block text-accent-400">Dental Store</span>
              </h1>
              <p className="text-xl mb-8 text-white/90">
                Discover 2,000+ premium dental products from 1,000+ trusted brands. 
                Everything you need for your dental practice, delivered to your doorstep.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link 
                  to="/products" 
                  className="bg-white text-primary-700 font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2"
                >
                  <ShoppingBagIcon className="h-5 w-5" />
                  <span>Shop Now</span>
                </Link>
                <button 
                  onClick={() => window.open('https://wa.me/918999250629', '_blank')}
                  className="border-2 border-white text-white font-semibold px-8 py-4 rounded-lg hover:bg-white hover:text-primary-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <PhoneIcon className="h-5 w-5" />
                  <span>WhatsApp Support</span>
                </button>
              </div>

              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-accent-400">2000+</div>
                  <div className="text-sm text-white/80">Products</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent-400">1000+</div>
                  <div className="text-sm text-white/80">Brands</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent-400">5K+</div>
                  <div className="text-sm text-white/80">Happy Customers</div>
                </div>
              </div>
            </div>

            {/* Infinite Client Photos Slider */}
            <div>
              <h2 className="text-3xl font-bold text-white text-center mb-6">Teethkart Clientele</h2>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md p-6">
                {/* Continuous scrolling container */}
                <div className="relative">
                  <div className="flex animate-scroll gap-6" style={{ width: 'fit-content' }}>
                    {/* First set of client photos */}
                    {clientPhotos.map((photo, index) => (
                      <div
                        key={`first-${index}`}
                        className="flex-shrink-0 w-80 group"
                      >
                        <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-300 group-hover:scale-105 group-hover:shadow-3xl">
                          <img
                            src={photo}
                            alt={`Teethkart Client ${index + 1}`}
                            className="w-full h-[450px] object-cover"
                            loading="eager"
                            onError={(e) => {
                              console.error(`Failed to load image ${index + 1}`);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    
                    {/* Duplicate set for seamless loop */}
                    {clientPhotos.map((photo, index) => (
                      <div
                        key={`second-${index}`}
                        className="flex-shrink-0 w-80 group"
                      >
                        <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-300 group-hover:scale-105 group-hover:shadow-3xl">
                          <img
                            src={photo}
                            alt={`Teethkart Client ${index + 1}`}
                            className="w-full h-[450px] object-cover"
                            loading="eager"
                            onError={(e) => {
                              console.error(`Failed to load image ${index + 1}`);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Dental Professionals Choose Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're committed to providing the best products and services to help your dental practice thrive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center hover:scale-105 transition-transform">
                <div className={`${feature.color} text-white p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-max section-padding">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Products</h2>
              <p className="text-gray-600">
                Handpicked products with the best value and quality for your practice.
              </p>
            </div>
            <Link 
              to="/products"
              className="btn-primary flex items-center space-x-2"
            >
              <span>View All</span>
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card p-6 animate-pulse">
                  <div className="bg-gray-300 h-48 rounded-lg mb-4"></div>
                  <div className="bg-gray-300 h-4 rounded mb-2"></div>
                  <div className="bg-gray-300 h-4 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <div key={product.id} className="card p-6 group">
                  <div className="relative mb-4 overflow-hidden rounded-lg">
                    <img
                      src={product.image_url || `https://via.placeholder.com/300x300/f0f9ff/0ea5e9?text=${encodeURIComponent(product.name)}`}
                      alt={product.name}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = `https://via.placeholder.com/300x300/f0f9ff/0ea5e9?text=${encodeURIComponent(product.name)}`;
                      }}
                    />
                    {product.discount_price && (
                      <span className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                        Save ₹{(product.price - product.discount_price).toLocaleString()}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{product.brand}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      {product.discount_price ? (
                        <>
                          <span className="text-xl font-bold text-green-600">
                            ₹{product.discount_price.toLocaleString()}
                          </span>
                          <span className="text-sm text-gray-500 line-through ml-2">
                            ₹{product.price.toLocaleString()}
                          </span>
                        </>
                      ) : (
                        <span className="text-xl font-bold text-gray-900">
                          ₹{product.price.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => addToCart(product)}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Google Reviews Section with Slider */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container-max section-padding">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <h2 className="text-3xl font-bold text-gray-900">Google Reviews</h2>
              <div className="flex items-center bg-white px-3 py-1 rounded-full shadow-sm">
                <StarIcon className="h-5 w-5 text-yellow-400 fill-current" />
                <span className="ml-1 font-bold text-gray-900">5.0</span>
              </div>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Trusted by thousands of dental professionals across India. Read what our customers have to say.
            </p>
            <a 
              href="https://www.google.com/search?sca_esv=cc001bef2c82a561&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-ExMzkjllQtIrOYflHeOgvyCLffBPfqxzQrfinFs910J449LyWiy3wxH1TdSrXJGzRJ-P6KjxVy-3MyM9qPsl0JeZub-dxTeO9ysm4f1KUXVOByYQMA%3D%3D&q=Group+Dental+Shopee+Reviews&sa=X&ved=2ahUKEwj1jdHx3eGQAxVfwTgGHao0NXgQ0bkNegQIHRAE&biw=1920&bih=945&dpr=1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium text-sm"
            >
              View all reviews on Google →
            </a>
          </div>

          {/* Desktop: Show 3 reviews side by side with slider */}
          <div className="hidden md:block relative">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentReview * (100 / 3)}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-1/3 flex-shrink-0 px-4">
                    <div className="card p-6 h-full bg-white shadow-lg hover:shadow-xl transition-shadow">
                      {/* Google logo badge */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {testimonial.source}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-4 italic min-h-[80px]">"{testimonial.content}"</p>
                      
                      <div className="border-t pt-4">
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.role}</div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="text-sm text-gray-500">{testimonial.location}</div>
                          <div className="text-xs text-gray-400">{testimonial.date}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevReview}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-gray-100 transition-colors z-10"
              aria-label="Previous review"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextReview}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-gray-100 transition-colors z-10"
              aria-label="Next review"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToReview(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentReview 
                      ? 'bg-primary-600 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to review ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Mobile: Show single review with slider */}
          <div className="md:hidden relative">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentReview * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4">
                    <div className="card p-6 bg-white shadow-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {testimonial.source}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                      
                      <div className="border-t pt-4">
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.role}</div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="text-sm text-gray-500">{testimonial.location}</div>
                          <div className="text-xs text-gray-400">{testimonial.date}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={prevReview}
                className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
                aria-label="Previous review"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToReview(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentReview 
                        ? 'bg-primary-600 w-6' 
                        : 'bg-gray-300'
                    }`}
                    aria-label={`Go to review ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextReview}
                className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
                aria-label="Next review"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
